// src/components/upload/ImageUploader.jsx
import React, { useEffect, useRef, useState } from "react";
import api from "@services/api";
import config from "@config/config";
import "./css/image_uploader.css";

const BASE_URL =
  (config && config.imageBaseUrl) ||
  process.env.REACT_APP_IMAGE_BASE_URL ||
  "";

function buildImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!BASE_URL) return path;
  const base = BASE_URL.replace(/\/$/, "");
  const clean = String(path).replace(/^\//, "");
  return `${base}/${clean}`;
}

/**
 * Reusable image uploader.
 *
 * Props:
 *  - value: string | undefined  (current image path/filename)
 *  - onChange: (newPath: string) => void  (called after successful upload)
 */
export default function ImageUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [currentPath, setCurrentPath] = useState(value || "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCurrentPath(value || "");
  }, [value]);

  const imageUrl = buildImageUrl(currentPath);

  function handleClick() {
    if (uploading) return;
    inputRef.current?.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      setError("");

      const res = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const data = res.data || {};
      const path = data.path || data.filename || "";

      if (path) {
        setCurrentPath(path);
        if (onChange) onChange(path); // pass only path to parent
      }
    } catch (err) {
      setError("Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  }

  return (
    <div className="oc-image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="oc-image-input-hidden"
        onChange={handleFileChange}
      />

      <div
        className={`oc-image-thumb ${uploading ? "is-uploading" : ""}`}
        onClick={handleClick}
        title={currentPath || "Click to upload image"}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="Image" />
        ) : (
          <span className="oc-image-thumb-placeholder">
            {uploading ? "..." : "Img"}
          </span>
        )}
      </div>

      {imageUrl && (
        <div className="oc-image-thumb-pop">
          <img src={imageUrl} alt="Preview" />
        </div>
      )}

      {error && <div className="oc-field-error">{error}</div>}
    </div>
  );
}

